import { reduceWhile } from 'ramda'
import xss from 'xss'

const createMasker = () => {
  const getMaskedString = (words: any) =>
    words
      .reduce(
        (message: any, increased: any) => message.concat(' ', increased.masked),
        '',
      )
      .trim()

  const sanitize = (message: any) => xss(message).replace(/ {2}/g, '\n')

  const expressionMasks = [
    // eslint-disable-next-line max-len
    {
      regex:
        /^4[0-9]{3}[-, ]?[0-9]{4}[-, ]?[0-9]{4}[-, ]?[0-9]{4}$|^3(?:0[0-5]|[68][0-9])[0-9]{11}$|^6(?:011|5[0-9]{2})[0-9]{12}$|^(?:2131|1800|35\d{3})\d{11}$/,
      tokenType: 'CREDIT_CARD_2',
      country: 'WWW',
    },
    {
      regex: /^;\d{0,19}=\d{7}\w*\?$/,
      tokenType: 'MAGNETIC_STRIPE_2',
      country: 'WWW',
    },
    {
      regex: /^%B\d{0,19}\^[\w\s/]{2,26}\^\d{7}\w*\?$/,
      tokenType: 'MAGNETIC_STRIPE',
      country: 'WWW',
    },
    // eslint-disable-next-line max-len
    {
      regex:
        /^[0-9]{4}[-, ]?[0-9]{4}[-, ]?[0-9]{4}[-, ]?[0-9]{4}$|^3[47][0-9]{13}$|^5[1-5][0-9]{2}[-, ]?[0-9]{4}[-, ]?[0-9]{4}[-, ]?[0-9]{4}$|^62[0-5][0-9]{13,16}$/,
      tokenType: 'CREDIT_CARD',
      country: 'WWW',
    },
    {
      regex: /^[0-8][0-9]{2}[- ]?[0-9]{2}[- ]?[0-9]{4}[ ]{0,20}$/,
      tokenType: 'SSN',
      country: 'US',
    },
    { regex: /^[a-zA-Z0-9]{10}$/, tokenType: 'SCHUFA_ID', country: 'DE' },
    {
      regex: /^[0-0]{2}\.?[0-9]{2}\.?[0-9]{2}-[0-9]{3}\.?[0-9]{2}$/,
      tokenType: 'NATIONAL_NUMBER',
      country: 'BE',
    },
    // eslint-disable-next-line max-len
    {
      regex:
        /^([A-Za-z]{6}[0-9lmnpqrstuvLMNPQRSTUV]{2}[abcdehlmprstABCDEHLMPRST]{1}[0-9lmnpqrstuvLMNPQRSTUV]{2}[A-Za-z]{1}[0-9lmnpqrstuvLMNPQRSTUV]{3}[A-Za-z]{1})|([0-9]{11})$/,
      tokenType: 'CODICE_FISCALE',
      country: 'IT',
    },
    {
      regex: /^[1-9][0-9]{10}$/,
      tokenType: 'IDENTIFICATION_NUMBER',
      country: 'TR',
    },
    { regex: /^[0-9]{4}$/, tokenType: 'SSN_LAST_FOUR_DIGITS', country: 'US' },
    {
      regex: /^LM-[\w]{13}|[a-zA_Z0-9]{10,30}$/,
      tokenType: 'LOYALTY_CARDS',
      country: 'WWW',
    },
    { regex: /^[0-9]{3,4}$/, tokenType: 'CVV_CVV2_CID2_CVC2', country: 'WWW' },
    // eslint-disable-next-line max-len
    {
      regex:
        /^[0-9]{4}[-, ]?[0-9]{4}[-, ]?[0-9]{4}[-, ]?[0-9]{4}$|^3[47][0-9]{13}$|^5[1-5][0-9]{2}[-, ]?[0-9]{4}[-, ]?[0-9]{4}[-, ]?[0-9]{4}$|^62[0-5][0-9]{13,16}$/,
      tokenType: 'ACCOUNT_NUMBER',
      country: 'WWW',
    },
    // eslint-disable-next-line max-len
    {
      regex:
        /^4[0-9]{3}[-, ]?[0-9]{4}[-, ]?[0-9]{4}[-, ]?[0-9]{4}$|^3(?:0[0-5]|[68][0-9])[0-9]{11}$|^6(?:011|5[0-9]{2})[0-9]{12}$|^(?:2131|1800|35\d{3})\d{11}$/,
      tokenType: 'ACCOUNT_NUMBER_CC1',
      country: 'WWW',
    },
    // eslint-disable-next-line max-len
    {
      regex:
        /^(?=(.*[a-z]){1,})(?=(.*[\d]){1,})(?=(.*[\W]){1,})(?!.*\s).{7,30}$/,
      tokenType: 'PASSWORD',
      country: 'WWW',
    },
    {
      regex: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
      tokenType: 'ACCOUNT_DISCOVER',
      country: 'WWW',
    },
  ]

  return (message) =>
    sanitize(
      getMaskedString(
        message
          .trim()
          .replace(/\t/g, ' ')
          .replace(/\r?\n\r?/g, '  ')
          .split(' ')
          .map((word) =>
            reduceWhile(
              (maskedWord) =>
                maskedWord.masked !== '*'.repeat(maskedWord.original.length),
              (maskedWord, expression) => {
                if (/.?[0-9]{3,}.?$/.test(maskedWord.original)) {
                  if (expression.regex.test(maskedWord.original)) {
                    maskedWord.masked = '*'.repeat(maskedWord.original.length)
                  }
                }
                return maskedWord
              },
              { original: word, masked: word },
              expressionMasks,
            ),
          ),
      ),
    )
}

export { createMasker }

/* 
    What is happening here? 

    These are the transformations that an input text ('message') goes through:
    a. message.trim() - remove leading and trailing spaces
    b. Replace \t , \r\n\r with one space and two spaces respectively
    c. message is split on empty space - output should be an array of text (array of 'word')

    d. this array (each word from the query) goes through a map. Each word is processed using ramda's reduceWhile. Documentation: https://ramdajs.com/docs/#reduceWhile
    Args:
    1. predicate function - checked before each step => while the word is not already fully masked into * (Continue if partially masked)
    2. action function - action to be performed on array element => if the word has 3+ consecutive digits, if the word matches any of the expressionMasks, then mask it as all *
    3. Accumulator - start start of result => { original: word, masked: word } where word is the current array element from step c.
    4. array to act on => expressionMasks array (Masking rules) 

    e. The final output of the map from step d is an array of objects. The object looks like { original, masked }. This is passed to getMaskedString
    f. getMasked String basically concats all 'masked' words and separates them using a space. It trims the final result.
    g. This masked phrase is passed to 'sanitize' that uses the xss package (Doc: https://www.npmjs.com/package/xss) with default config to sanitize unsafeHTML
    h. It also replaces two consecutive spaces with a newline character.

*/
